import { FULLTEXT_REVIEW, FULLTEXT_REVIEW_SOURCE, RULES_LINKS, RULES_LINKS_NOFIRM } from "./const";
import FirmReviewStarsPicker from "./stars-picker";
import { node } from "~/util/dom";
import { CONFIG } from "~/config";
import { showAlternativesPOI } from "./alternate";
import { store } from "~/state/store";
import { getFirmLayout } from "~/util/review";
import { putReview, getDetail } from "~/util/providers/review";
import { putToCacheWithoutDetail } from "~/util/reviews-cache";
import { IReviewItem, IReview } from "./interfaces";
import { IReviewConf } from "~/interfaces";
import { _ } from "~/i18n";
import LoaderDialog from "~/es6/loader-dialog";
import Notifications, { NOTIFICATIONS_TYPE } from "~/util/notifications";

interface IFirmStarsReview {
	data: IReviewConf;
	starWidth?: number;
	starOffset?: number;
	logSource?: string;
	logPosition?: string;
	logRecenzeHvezdy?: boolean;
	setVisitTitle?: boolean;
	loaderStart?: () => void;
	loaderEnd?: () => void;
	onLoginError?: (data: { isOwner: boolean; myReview: IReviewItem }) => void;
	showPopupCb?: (data: { review: IReview; myReview: IReviewItem }) => void;
	onStructuredRating?: (data: { review: IReview; rating: number; source: string; id: number | string; }) => void;
	onError?: (error: Error) => void;
	activeRequest?: boolean;
	logObj?: object;
	fromOtherPOI?: boolean,
	rating?: number;
}

// zobrazeni hvezdicek + hodnoceni, podminek
export default class FirmStarsReview {
	_opts: IFirmStarsReview;

	_dom: {[key: string]: HTMLElement};

	_putReviewId: number | string;

	_activeRequest: boolean;

	_loader: null | { close: () => void };

	_starsPicker: FirmReviewStarsPicker;

	_detailReview: IReview;

	_myReview: IReviewItem;

	constructor(optsArg: IFirmStarsReview) {
		const opts = {
			data: null,
			starWidth: 30, // sirka a vyska hvezdy [px]
			starOffset: 10, // prostor mezi hvezdami [px]
			logSource: "recenze-poi",
			logPosition: "default",
			logRecenzeHvezdy: true, // logovat event?
			setVisitTitle: false, // nastavit visit title pro stars picker
			fromOtherPOI: false,
			// dve fce - vytvoreni a zavreni loaderu
			loaderStart: () => {
				this._loaderStart();
			},
			loaderEnd: () => {
				this._loaderEnd();
			},
			onLoginError: () => { /**/ }, // data - chyba - data.isOwner; data.isMyReview
			showPopupCb: () => { /**/ }, // putReviewData ulozil jsem pres hvezdicky hodnoceni, jdu dal
			onStructuredRating: () => { /**/ },
			onError: () => { /**/ }, // e - nepodarilo se ulozit hodnoceni
			logObj: {}, // pridavna analytika v objektu
			...optsArg,
		};

		this._opts = opts;
		this._putReviewId = this._opts.data.id;
		this._dom = {
			container: node("div", { className: "firm-review-stars-review" }),
		};
		this._activeRequest = this._opts.activeRequest || false;
		this._loader = null;
		this._starsPicker = new FirmReviewStarsPicker({
			starWidth: this._opts.starWidth,
			starOffset: this._opts.starOffset,
			clickCb: data => this._starsPickerClick(data),
		});
		this._detailReview = null;
		this._myReview = this._opts.data.myReview || null;

		if (this._opts.rating) {
			this._starsPicker.percent = this._opts.rating;
		}

		if (this._opts.setVisitTitle) {
			this._starsPicker.setVisitTitle();
		} else {
			this._starsPicker.setDefaultTitle();
		}

		this._build();

		if (this._opts.data.openReviewPopup?.stars > 0) {
			this._openStarsReviewPopup(JSON.parse(JSON.stringify(this._opts.data.openReviewPopup)), true);
		}

		if (this._opts.data.openReviewPopup) {
			delete this._opts.data.openReviewPopup;
		}
	}

	destroy() {
		this._dom.container.remove();
	}

	get container() {
		return this._dom.container;
	}

	get responseEl() {
		return this._dom.responseEl;
	}

	get agreement() {
		return this._dom.agreement;
	}

	_build() {
		// dekujeme
		const responseEl = node("p", { className: "response" });
		// smluvni podminky
		const lang = CONFIG.defaultLang;
		const rulesSource = this._opts.data.isFirm ? RULES_LINKS : RULES_LINKS_NOFIRM;
		const rulesLink = Object.keys(rulesSource).indexOf(lang) > -1 ? rulesSource[lang] : rulesSource.en;
		const termsLink = `https://licence.mapy.${CONFIG.TLD}/?doc=mapy_lu&lang=${lang}`;
		const agreement = node("p", {
			className: "licence",
			innerHTML: _("detail.content.review2.starsLicence", {
				linkAgrStart: `<a href="${termsLink}" target="_blank">`,
				linkAgrEnd: "</a>",
				linkRulStart: `<a href="${rulesLink}" target="_blank">`,
				linkRulEnd: "</a>",
			}),
		});
		const linksEl = Array.from(agreement.querySelectorAll("a"));

		linksEl.forEach((item, ind) => {
			item.setAttribute("data-log-ignore", "true");
			item.addEventListener("pointerdown", () => {
				this._log("click", {
					url: item.href,
					text: ind === 0 ? "podmínkami" : "pravidly",
				});
			});
		});
		this._dom.container.appendChild(this._starsPicker.container);
		this._starsPicker.container.appendChild(responseEl);
		this._dom.container.appendChild(agreement);
		Object.assign(this._dom, { responseEl, agreement });
	}

	_starsPickerClick(starsData) {
		// zakazeme dalsi hodnoceni
		this._starsPicker.enabled = false;
		this._dom.responseEl.innerHTML = "";

		if (this._opts.logRecenzeHvezdy) {
			// analytika
			this._log("click", {
				text: "recenze-hvezdy",
			});
		}

		this._openStarsReviewPopup(starsData, this._opts.fromOtherPOI);
	}

	_openStarsReviewPopup(starsData, forceOpen = false) {
		const { user } = store.getState().user;

		(user.state === "green"
			? Promise.resolve()
			: Mapy.getComponent("login").open({
				analyticsData: {
					loginOrigin: "reviewsAdd",
					section: "reviews",
				},
				motivation: "review",
			})
		).then(async () => {
			const isAlternatives = this._opts.data.review?.alternatives?.length;
			const open = forceOpen && starsData?.stars > 0;

			if (!isAlternatives || Mapy.CUS.review?.webcomponent || (isAlternatives && (open || (!forceOpen && await showAlternativesPOI(this._opts.data, this._opts.data.review?.alternatives, starsData, {
				...this._opts.logSource ? { source: this._opts.logSource } : {},
				...this._opts.logPosition ? { position: this._opts.logPosition } : {},
			}))))) {
				// loader
				this._opts.loaderStart();
				// ulozime
				(user.state === "green" ? Promise.resolve(this._opts.data) : getDetail({ source: this._opts.data.source, id: this._opts.data.id })).then(data => {
					// analytika hvezd
					const starsCount = ((starsData.stars - 1) * 2) + (starsData.full ? 2 : 1);

					// analytika
					this._log("click", {
						text: `stars-set-${starsCount}`,
					});

					const loginOutput = {
						isOwner: data.isOwner,
						myReview: data.myReview,
					};

					if (loginOutput.isOwner || loginOutput.myReview) {
						this._opts.loaderEnd();
						this._opts.onLoginError(loginOutput);

						return;
					}

					const hasStructuredRating = Array.isArray(data.review?.structured_rating) && data.review.structured_rating.length;

					if (hasStructuredRating) {
						this._opts.loaderEnd();
						// preskocime putreview
						this._opts.onStructuredRating && this._opts.onStructuredRating({
							review: data.review,
							rating: starsData.percent,
							source: this._opts.data.source,
							id: this._opts.data.id,
						});

						return;
					}

					putReview({
						source: this._opts.data.source,
						...this._opts.logSource === FULLTEXT_REVIEW
							? {
								reviewSource: FULLTEXT_REVIEW_SOURCE,
							}
							: {},
						poiId: this._opts.data.id,
						text: "",
						mediaIds: [],
						rating: starsData.percent,
						activeRequest: this._activeRequest,
						structuredRating: [],
					}).then(putReviewData => {
						this._detailReview = {
							...this._opts.data.review,
							...putReviewData.review,
						};
						this._myReview = putReviewData.myReview;

						// ulozime do kese
						putToCacheWithoutDetail(putReviewData, this._opts.data.source, this._opts.data.id);
						this._opts.loaderEnd();
						this._showSuccess();
						this._opts.showPopupCb({
							review: this._detailReview,
							myReview: this._myReview,
						});
					}, error => {
						// po failu uz nechceme logovat
						this._opts.logRecenzeHvezdy = false;
						this._opts.loaderEnd();
						this._showError();
						this._opts.onError(error);
					});
				}, () => {
					// zobrazime defaultni stav
					this._opts.loaderEnd();
					this._starsPickerReset();
					this._dom.responseEl.innerHTML = "";
				});
			} else {
				// zobrazime defaultni stav
				this._opts.loaderEnd();
				this._starsPickerReset();
				this._dom.responseEl.innerHTML = "";
			}
		});
	}

	_showSuccess() {
		// zobrazime diky
		this._dom.responseEl.innerHTML = "";

		if (!this._dom.container.parentNode) {
			Notifications.notify({
				content: _("detail.content.review2.saveThankYouNotify"),
				type: NOTIFICATIONS_TYPE.SUCCESS,
			});
		}
	}

	_showError() {
		// zobrazime chybu
		this._dom.responseEl.innerHTML = "";
		this._starsPickerReset();

		const textEl = node("span", { className: "text error", textContent: _("detail.content.review2.putReviewError") });

		this._dom.responseEl.appendChild(textEl);
	}

	_starsPickerReset() {
		this._starsPicker.active = true;
		this._starsPicker.enabled = true;
		this._starsPicker.percent = 0;
	}

	_loaderStart() {
		if (this._loader) {
			return;
		}

		this._loader = new LoaderDialog();
	}

	_loaderEnd() {
		if (!this._loader) {
			return;
		}

		this._loader.close();
		this._loader = null;
	}

	_log(name: string, data?: object) {
		/* eslint-disable camelcase */
		Mapy.getComponent("logger").log(name, {
			position: this._opts.logPosition,
			rating_layout: getFirmLayout(this._opts.data.review, this._opts.data.booking, this._myReview, this._opts.data.isFirm),
			source: this._opts.logSource,
			sourcePoi: this._opts.data.source,
			typeid: this._opts.data.typeId,
			firmTwin: this._opts.data.isTwin ? 1 : 0,
			section: "reviews",
			type: "recenze",
			...this._opts.logObj,
			...data,
		});
	}
}
